import isEqual from "lodash.isequal";
import { v4 } from "uuid";

import { SharedTextMod, SharedTextModSchema } from "@intelligems/ig-types";
import { sharedFromAiFormat } from "@src/store/slices/experienceBuilder/common/utils/sharedFromAiFormat";
import { pickSharedFields } from "@src/store/slices/experienceBuilder/common/utils/pickSharedFields";
import { ModHandler, TextMod } from "@src/store/slices/experienceBuilder/types";
import { isValidSelector } from "@src/store/slices/experienceBuilder/common/utils/html/isValidSelector";

export const textModHandler: ModHandler<TextMod> = {
  normalize: (mod) => ({
    ...mod,
    selector: mod.selector.trim(),
    find: mod.find.trim(),
    replace: mod.replace.trim(),
  }),

  convertTo: (conversion) => {
    if (conversion.type !== "onsiteEdit") {
      throw new Error("Expected onsiteEdit");
    }

    const edit = conversion.dto;
    const editMode = edit.onsiteEditType === "html" ? "html" : "text";
    const selector = edit.querySelectors.join(",");
    return {
      id: edit.id,
      category: "basicMod",
      type: "text",
      groupId: edit.groupId,
      selector,
      description: edit.title ?? undefined,
      replace: editMode === "text" ? edit.replace : "",
      html: editMode === "html" ? edit.replace : "",
      find: edit.find ?? "",
      remove: edit.remove,
      editMode,
      insertType: edit.insertType ?? "replace",
      variationId: edit.variationId,
      nodes: [],
      createdAtUrl: edit.createdAtUrl ?? undefined,
      source: edit.source ?? undefined,
    };
  },

  convertFrom: (mod) => ({
    type: "onsiteEdit",
    dto: {
      id: mod.id,
      groupId: mod.groupId,
      variationId: mod.variationId,
      replace: mod.editMode === "html" ? mod.html : mod.replace,
      find: mod.find,
      querySelectors: [mod.selector],
      isRegex: false,
      skip: false,
      remove: mod.remove,
      isHtml: false,
      isImage: false,
      onsiteEditType: mod.editMode,
      insertType: mod.insertType === "replace" ? null : mod.insertType,
      createdAtUrl: mod.createdAtUrl,
      source: mod.source ?? null,
      title: mod.description ?? null,
    },
  }),

  getErrors: (mod) => {
    const errors: Record<string, string> = {};
    const content = mod.editMode === "html" ? mod.html : mod.replace;

    if (!mod.selector) {
      errors.selector = "Element selection is required";
    }
    if (mod.selector && !isValidSelector(mod.selector)) {
      errors.selector = "Selector is invalid";
    }
    if (!content && !mod.remove) {
      if (mod.editMode === "html") {
        errors.html = "HTML or hide element is required";
        errors.remove = "HTML or hide element is required";
      } else {
        errors.replace = "Replace or hide element is required";
        errors.remove = "Replace or hide element is required";
      }
    }
    return errors;
  },

  isEquivalent: (a, b) => b.type === "text" && a.selector === b.selector,

  defaults: () => ({
    id: v4(),
    type: "text",
    category: "basicMod",
    variationId: "",
    editMode: "text",
    selector: "",
    find: "",
    replace: "",
    html: "",
    remove: false,
    insertType: "replace",
    nodes: [],
  }),

  toAiFormat: (mod) =>
    pickSharedFields<SharedTextMod>(SharedTextModSchema, mod),

  fromAiFormat: (aiMod, context) => ({
    ...(context.existing ?? textModHandler.defaults()),
    ...aiMod,
    ...sharedFromAiFormat(aiMod, context),
  }),

  hasChanges: (oldMod, newMod) => {
    const normalize = (mod: TextMod) => ({
      description: mod.description,
      editMode: mod.editMode,
      selector: mod.selector,
      find: mod.find,
      replace: mod.replace,
      // Only compare html when editMode is "html" - for text mode, html is just cached DOM state
      ...(mod.editMode === "html" && { html: mod.html }),
      remove: mod.remove,
      insertType: mod.insertType,
    });
    return !isEqual(normalize(oldMod), normalize(newMod));
  },
};
